import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { CompareService } from '../../services/compare.service';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../environments/environmentCAS';
import { MessageService } from 'primeng/api';
import { EnterpriseHistoryService } from '../../services/enterprise-history.service';
import { FileUploadModule } from 'primeng/fileupload';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';

interface EnterpriseFormula {
  level_name: string;
  is_selected?: boolean;
  benefits: { [category: string]: { [guarantee: string]: string } };
  premiums: { [category: string]: number };
}

interface EnterpriseExtraction {
  insurer: string;
  contract_name: string;
  formulas: EnterpriseFormula[];
  client_name?: string;
  client_contact_name?: string;
  client_email?: string;
  client_civility?: string;
  client_identity?: {
    raison_sociale?: string;
    interlocuteur?: string;
    proposant?: string;
    nom?: string;
    civilite?: string;
    email?: string;
  };
  interlocuteur?: string;
  proposant?: string;
  nom?: string;
  raison_sociale?: string;
  sourceFileName?: string;
  isCurrentContract?: boolean;
  clientName?: string;
  timestamp?: number;
}

interface EnhancedFormula extends EnterpriseFormula {
  insurer: string;
  contract: string;
  coverageScore: number;
  valueScore: number;
  isBestPrice?: boolean;
  isBestCoverage?: boolean;
  isBestValue?: boolean;
  isCurrentContract?: boolean;
  clientName?: string;
  extractionId?: number;
}

interface RenfortComparisonColumn {
  base: EnhancedFormula;
  renfort: EnhancedFormula | null;
}

type SortType = 'none' | 'price' | 'coverage' | 'recommended';
type BenefitRow = { label: string; paths: string[] };
type ComparisonHistoryItem = {
  id: string;
  createdAt: string;
  clientName: string;
  formulasCount: number;
  includesCurrentContract: boolean;
  extractions: EnterpriseExtraction[];
};

@Component({
  selector: 'app-enterprise-compare',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FileUploadModule,
    CardModule,
    ProgressSpinnerModule,
    ToastModule,
    DialogModule,
    InputTextModule,
    ButtonModule
  ],
  providers: [MessageService],
  templateUrl: './enterprise-compare.component.html',
  styleUrls: ['./enterprise-compare.component.css']
})
export class EnterpriseCompareComponent implements OnInit {
  extractions: EnterpriseExtraction[] = [];
  /** Fichiers PDF uploadés pour la comparaison — joints à l'email de devis */
  private comparisonSourceFiles: File[] = [];
  isLoading = false;
  selectedSort: SortType = 'recommended';
  showOptions = false;
  detectedClientName = '';
  passValue = 48060; // Valeur demandée par l'utilisateur
  comparisonHistory: ComparisonHistoryItem[] = [];
  readonly maxVisibleColumns = Number.MAX_SAFE_INTEGER;
  readonly maxVisibleOptionColumns = 10;
  private readonly historyStorageKey = 'enterprise_comparison_history';
  private uploadRequestId = 0;
  private activeUploadRequestId: number | null = null;

  /** Dialogue envoi email : nom client, civilité, aperçu tableau, email destinataire */
  showEmailDialog = false;
  recipientEmailEntreprise = '';
  /** Nom affiché dans l'email (auto depuis PDF / IA, modifiable) */
  emailClientDisplayName = '';
  /** Monsieur | Madame | '' */
  emailClientCivility: '' | 'Monsieur' | 'Madame' = '';
  readonly civilityOptions: { label: string; value: '' | 'Monsieur' | 'Madame' }[] = [
    { label: '(non précisé)', value: '' },
    { label: 'Monsieur', value: 'Monsieur' },
    { label: 'Madame', value: 'Madame' }
  ];
  emailTableHtmlRaw = '';
  emailTablePreview: SafeHtml | null = null;
  sendingEnterpriseEmail = false;

  /** Dialogue demande d'information */
  showDemandeInfoDialog = false;
  demandeInfoClientName = '';
  demandeInfoCivility: '' | 'Monsieur' | 'Madame' = '';
  demandeInfoCompanyName = '';
  demandeInfoRecipientEmail = '';
  demandeInfoSiret = '';
  demandeInfoGerant = '';
  demandeInfoSalarie = '';
  demandeInfoCcn = '';
  sendingDemandeInfo = false;

  /** Dialogue email economiser */
  showEconomiserEmailDialog = false;
  economiserClientName = '';
  economiserCivility: '' | 'Monsieur' | 'Madame' = 'Monsieur';
  economiserRecipientEmail = '';
  economiserAssurance = '';
  economiserRef = '';
  economiserProduit = '';
  sendingEconomiserEmail = false;

  sortOptions: { label: string, value: SortType, icon: string }[] = [
    { label: 'Recommandé', value: 'recommended', icon: 'pi pi-thumbs-up' },
    { label: 'Moins cher', value: 'price', icon: 'pi pi-dollar' },
    { label: 'Meilleures garanties', value: 'coverage', icon: 'pi pi-shield' },
    { label: 'Aucun tri', value: 'none', icon: 'pi pi-list' }
  ];

  // List of unique benefit categories and guarantees for the table rows
  benefitCategories: string[] = ['HOSPITALISATION', 'DENTAIRE', 'OPTIQUE', 'SOINS_COURANTS'];
  readonly displayRows: BenefitRow[] = [
    // 1) Lignes demandées (ordre UI)
    { label: 'Hospitalisation', paths: ['HOSPITALISATION.honoraires_chirurgien_optam', 'HOSPITALISATION.frais_sejour', 'HOSPITALISATION.hospitalisation'] },
    { label: 'Chambre Particulière', paths: ['HOSPITALISATION.chambre_particuliere'] },
    { label: 'Honoraire', paths: ['HOSPITALISATION.honoraires_chirurgien_optam', 'HOSPITALISATION.honoraires'] },
    { label: 'Prothese Fixe', paths: ['DENTAIRE.prothese_fixe', 'DENTAIRE.soins_dentaires'] },
    { label: 'Forfait dentaire (implantologie)', paths: ['DENTAIRE.implantologie', 'DENTAIRE.Implantology', 'DENTAIRE.implantology'] },
    { label: 'Orthodontie', paths: ['DENTAIRE.orthodontie', 'DENTAIRE.Orthodontie', 'DENTAIRE.orthodontia', 'DENTAIRE.Orthodontia'] },
    { label: 'OPTIQUE (Verres Complexes + Monture)', paths: ['OPTIQUE.verres_complexes_monture', 'OPTIQUE.verres_complexes'] },
    { label: 'Medecine complementaire', paths: ['BIEN_ETRE.medecines_douces', 'SOINS_COURANTS.medecine_complementaire', 'SOINS_COURANTS.consultation_generaliste_optam'] }
  ];

  constructor(
    private compareService: CompareService,
    private messageService: MessageService,
    private http: HttpClient,
    private authService: AuthService,
    private domSanitizer: DomSanitizer,
    private enterpriseHistoryService: EnterpriseHistoryService,
    private router: Router
  ) { }

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    if (!user || (user.email !== 'contact@brokin.fr' && user.email !== 'contact@brokins.fr')) {
      this.messageService.add({
        severity: 'error',
        summary: 'Accès refusé',
        detail: 'Le comparateur entreprise est strictement réservé à l\'adresse contact@brokins.fr.'
      });
      this.router.navigate(['/']);
      return;
    }
    this.loadHistory();
  }

  async onUpload(event: any, asCurrentContract = false): Promise<void> {
    const files: File[] = event.files;
    if (files.length === 0) return;

    const requestId = ++this.uploadRequestId;
    this.activeUploadRequestId = requestId;
    this.isLoading = true;

    const filesArray = Array.from(files);
    const batchResults: EnterpriseExtraction[] = [];

    try {
      // Process all files in parallel for faster turnaround.
      const settledResults = await Promise.allSettled(
        filesArray.map((file) => firstValueFrom(this.compareService.extractEnterpriseQuotes(file, this.passValue)))
      );

      if (this.activeUploadRequestId !== requestId) {
        return;
      }

      settledResults.forEach((result, index) => {
        const file = filesArray[index];
        if (result.status === 'fulfilled') {
          const res = result.value;
          if (res && !res.error) {
            const extraction = res as EnterpriseExtraction;
            extraction.sourceFileName = file.name;
            extraction.isCurrentContract = asCurrentContract;
            extraction.clientName = this.resolveClientDisplayNameFromExtraction(extraction, file.name);
            batchResults.push(extraction);
            this.comparisonSourceFiles.push(file);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur d\'extraction',
              detail: `Le fichier ${file.name} n'a pas pu être analysé: ${res?.error || 'Erreur inconnue'}`
            });
          }
        } else {
          console.error(`Upload error for ${file.name}:`, result.reason);
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur serveur',
            detail: `Le fichier ${file.name} a échoué (service temporairement indisponible).`
          });
        }
      });

      // Professional UX: render all extracted results in one shot when the batch ends.
      if (batchResults.length > 0) {
        // Keep a single "current client quote" context: when user uploads client quote later,
        // previous formulas are still compared but no longer tagged as current.
        if (asCurrentContract) {
          this.extractions = this.extractions.map((item) => ({
            ...item,
            isCurrentContract: false
          }));
        }
        if (!this.detectedClientName) {
          this.detectedClientName = this.resolveCompanyNameFromBatch(batchResults) || this.resolveClientNameFromBatch(batchResults);
        }
        this.extractions.push(...batchResults);
        this.saveCurrentComparisonToHistory(false);
      }

      if (batchResults.length > 0) {
        this.messageService.add({
          severity: 'success',
          summary: 'Extraction terminée',
          detail: this.buildUploadSuccessMessage(batchResults.length, asCurrentContract)
        });
      }
    } finally {
      if (this.activeUploadRequestId === requestId) {
        this.isLoading = false;
        this.activeUploadRequestId = null;
      }
    }
  }

  cancelUpload(): void {
    if (!this.isLoading) return;
    this.activeUploadRequestId = null;
    this.isLoading = false;
    this.messageService.add({
      severity: 'warn',
      summary: 'Chargement annulé',
      detail: 'L’import en cours a été annulé.'
    });
  }

  hasCurrentContractQuote(): boolean {
    return this.extractions.some((item) => !!item.isCurrentContract);
  }

  hasOtherQuotes(): boolean {
    return this.extractions.some((item) => !item.isCurrentContract);
  }

  canShowComparisonTable(): boolean {
    return this.getFlatFormulas(false).length > 0 || (this.showOptions && this.getFlatFormulas(true).length > 0);
  }

  private buildUploadSuccessMessage(uploadedCount: number, asCurrentContract: boolean): string {
    const currentCount = this.extractions.filter((item) => !!item.isCurrentContract).length;
    const otherCount = this.extractions.filter((item) => !item.isCurrentContract).length;

    if (asCurrentContract && otherCount === 0) {
      return `Devis client attaché (${uploadedCount}). Ajoutez d'autres devis pour afficher le tableau de comparaison.`;
    }

    if (!asCurrentContract && currentCount === 0) {
      return `${uploadedCount} devis attaché(s). Le tableau s'affichera dès que le devis client sera ajouté.`;
    }

    return `${uploadedCount} devis analysé(s) avec succès. Tableau de comparaison mis à jour.`;
  }

  // Scoring logic
  private parseBenefitValue(val: string): number {
    if (!val || val === '-' || val.toLowerCase() === 'néant' || val.toLowerCase() === 'neant') {
      return 0;
    }
    if (val.toLowerCase().includes('frais réels') || val.toLowerCase().includes('fr')) return 1000;

    const match = val.match(/(\d+)[\s]*[%€]/);
    return match ? parseInt(match[1], 10) : 0;
  }

  private calculateCoverageScore(formula: any): number {
    let score = 0;
    let items = 0;

    for (const category of this.benefitCategories) {
      const guarantees = formula.benefits[category];
      if (guarantees) {
        Object.values(guarantees).forEach((g: any) => {
          score += this.parseBenefitValue(g);
          items++;
        });
      }
    }
    return items > 0 ? Math.round(score / items) : 0;
  }

  private buildFormulaSignature(ext: any, formula: any): string {
    // 1. Normalize Row Values
    const rowValues = this.displayRows
      .map((row) => {
        const raw = (this.getDisplayRowValue(formula, row) || '');
        const val = raw
          .replace(/<[^>]*>/g, '') // Supprime le HTML
          .replace(/\s+/g, ' ')    // Normalise les espaces
          .trim()
          .toUpperCase();
        return val;
      })
      .join('|');

    // 2. Normalize Premiums
    const premiumValues = ['isole', 'duo', 'famille']
      .map((cat) => {
        const p = formula.premiums?.[cat];
        const num = (p != null && !isNaN(Number(p))) ? Number(p) : 0;
        return num > 0 ? num.toFixed(2) : '0.00';
      })
      .join('|');

    const normInsurer = (ext.insurer || '').replace(/\s+/g, '').toUpperCase();

    // La signature se base sur l'assureur, les garanties et les tarifs.
    // On exclut le nom du niveau pour fusionner les colonnes identiques (ex: Option 1 vs Option 1 (Base 350))
    return `${normInsurer}#${rowValues}#${premiumValues}`;
  }

  private isOptionOrRenfort(formula: EnterpriseFormula, contractName?: string): boolean {
    const levelText = (formula?.level_name || '').trim().toLowerCase();
    const price = formula.premiums?.['isole'] || 0;

    // 1. Explicit keywords for renforts
    if (/(renfort|r[1-9]|module\s+\d+|pack\s+r|surcomplementaire|sur-?comp)/i.test(levelText)) return true;
    if (levelText.startsWith('+')) return true;

    // 2. Ambiguous "Option" keyword:
    // If it's called "Option X" but price is relatively high, it's likely a base formula level.
    if (/^option\s*\d+$/i.test(levelText) && price > 40) return false;

    // Otherwise, "Option" or "Opt" usually means a renfort/option
    if (/(option|opt\.)/i.test(levelText)) return true;

    return false;
  }

  private hashCode(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++)
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    return h;
  }

  private normalizeInsurerName(insurer: string): string {
    const normalized = (insurer || '').trim().toUpperCase();
    if (normalized === 'MILTIS') {
      return 'ALPTIS';
    }
    return insurer;
  }

  getFlatFormulas(includeOptions = false): EnhancedFormula[] {
    const flatBySignature = new Map<string, EnhancedFormula>();
    this.extractions.forEach((ext, index) => {
      const formulasToDisplay = (ext.formulas || []).filter((formula) =>
        includeOptions
          ? this.isOptionOrRenfort(formula, ext.contract_name)
          : !this.isOptionOrRenfort(formula, ext.contract_name)
      );

      for (const formula of formulasToDisplay) {
        const insurer = this.normalizeInsurerName(ext.insurer);
        const levelName = (formula.level_name || '').trim();
        const price = formula.premiums['isole'] || 0;

        // Second layer of de-duplication: Insurer + Level Name + Price
        // This handles cases where signatures might differ slightly but formulas are logically identical
        const displayKey = `${insurer.toUpperCase()}#${levelName.toUpperCase()}#${price.toFixed(2)}`;

        const signature = this.buildFormulaSignature(ext, formula);
        const coverageScore = this.calculateCoverageScore(formula);

        // Use a stable ID based on extraction properties to prevent flickering
        const stableId = ext.timestamp || (ext.sourceFileName ? this.hashCode(ext.sourceFileName + ext.insurer) : index);

        const candidate: EnhancedFormula = {
          extractionId: stableId,
          insurer,
          contract: ext.contract_name,
          coverageScore,
          valueScore: coverageScore / (price || 1),
          isCurrentContract: !!ext.isCurrentContract,
          clientName: ext.clientName || '',
          ...formula
        };

        const existingBySignature = flatBySignature.get(signature);
        const existingByDisplay = Array.from(flatBySignature.values()).find(f =>
          f.insurer.toUpperCase() === insurer.toUpperCase() &&
          (f.level_name || '').trim().toUpperCase() === levelName.toUpperCase() &&
          (f.premiums['isole'] || 0).toFixed(2) === price.toFixed(2)
        );

        const existing = existingBySignature || existingByDisplay;

        if (!existing) {
          flatBySignature.set(signature, candidate);
          continue;
        }

        // Priorités en cas de doublon :
        // 1. Préférer le contrat marqué comme "actuel"
        // 2. Sinon, préférer le score de couverture le plus élevé
        const isNewPreferable = (!existing.isCurrentContract && candidate.isCurrentContract) ||
          (candidate.isCurrentContract === existing.isCurrentContract &&
            candidate.coverageScore > existing.coverageScore);

        if (isNewPreferable) {
          // If we replace by display key, we must remove the old signature entry
          const oldSignature = Array.from(flatBySignature.entries()).find(([sig, f]) => f === existing)?.[0];
          if (oldSignature) flatBySignature.delete(oldSignature);
          flatBySignature.set(signature, candidate);
        }
      }
    });
    let flat = Array.from(flatBySignature.values());

    // Determine badges (exclude current contract from being recommended)
    if (flat.length > 0) {
      const comparisonCandidates = flat.filter(f => !f.isCurrentContract);
      
      let minPrice = 999999;
      let maxCoverage = -1;
      let maxValue = -1;

      if (comparisonCandidates.length > 0) {
        minPrice = Math.min(...comparisonCandidates.map(f => f.premiums['isole'] || 999999));
        maxCoverage = Math.max(...comparisonCandidates.map(f => f.coverageScore));
        maxValue = Math.max(...comparisonCandidates.map(f => f.valueScore));
      }

      flat = flat.map(f => {
        if (f.isCurrentContract) {
          return {
            ...f,
            isBestPrice: false,
            isBestCoverage: false,
            isBestValue: false
          };
        }
        return {
          ...f,
          isBestPrice: f.premiums['isole'] === minPrice,
          isBestCoverage: f.coverageScore === maxCoverage,
          isBestValue: f.valueScore === maxValue
        };
      });
    }

    // Sort
    if (this.selectedSort === 'price') {
      flat.sort((a, b) => (a.premiums['isole'] || 9999) - (b.premiums['isole'] || 9999));
    } else if (this.selectedSort === 'coverage') {
      flat.sort((a, b) => b.coverageScore - a.coverageScore);
    } else if (this.selectedSort === 'recommended') {
      flat.sort((a, b) => b.valueScore - a.valueScore);
    }

    // Le client a demandé à ce que le contrat actuel sorte de la comparaison et ne soit pas affiché
    const otherFormulas = flat.filter((f) => !f.isCurrentContract);
    flat = [...otherFormulas];

    return flat;
  }

  getVisibleFormulas(): EnhancedFormula[] {
    return this.getFlatFormulas(false).slice(0, this.maxVisibleColumns);
  }

  getVisibleOptionFormulas(): EnhancedFormula[] {
    if (!this.showOptions) return [];

    const visibleInsurers = new Set(this.getVisibleFormulas().map(f => f.insurer));

    return this.getFlatFormulas(true)
      .filter(opt => visibleInsurers.has(opt.insurer))
      .slice(0, 10); // Use a reasonable limit or a constant
  }

  getHiddenOptionFormulasCount(): number {
    if (!this.showOptions) return 0;
    const visibleBase = this.getVisibleFormulas();
    const baseInsurers = new Set(visibleBase.map((formula) => formula.insurer));
    const syncedCount = this.getFlatFormulas(true)
      .filter((formula) => baseInsurers.has(formula.insurer))
      .length;
    return Math.max(0, syncedCount - this.maxVisibleOptionColumns);
  }

  getRenfortComparisonColumns(): RenfortComparisonColumn[] {
    if (!this.showOptions) return [];

    const baseColumns = this.getVisibleFormulas();
    const options = this.getVisibleOptionFormulas();

    return baseColumns.map((base) => {
      const sameInsurerAndProduct = options.find(
        (opt) => opt.insurer === base.insurer && opt.contract === base.contract
      );
      const sameInsurer = options.find((opt) => opt.insurer === base.insurer);

      return {
        base,
        renfort: sameInsurerAndProduct || sameInsurer || null
      };
    });
  }

  getRenfortForBase(base: EnhancedFormula): EnhancedFormula | null {
    const match = this.getRenfortComparisonColumns().find(
      (col) =>
        col.base.insurer === base.insurer &&
        col.base.contract === base.contract &&
        col.base.level_name === base.level_name
    );
    return match?.renfort || null;
  }

  hasAnyVisibleRenfort(): boolean {
    return this.getVisibleFormulas().some(f => !!this.getRenfortForBase(f));
  }

  getVisibleFormulasWithRenfort(): EnhancedFormula[] {
    return this.getVisibleFormulas().filter(f => !!this.getRenfortForBase(f));
  }

  findBaseForOption(option: EnhancedFormula): EnhancedFormula | null {
    const optionLevel = (option.level_name || '').toLowerCase();

    // 1. Filter formulas from the SAME extraction (same PDF)
    const candidates = this.getFlatFormulas(false).filter(f =>
      f.extractionId === option.extractionId && !this.isOptionOrRenfort(f, f.contract)
    );

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    // 2. If multiple bases in same PDF, match by project/module name similarity
    const projectMatch = optionLevel.match(/(projet|module|formule|niveau)\s*(\d+)/i);
    const projectPrefix = projectMatch ? projectMatch[0].toLowerCase() : null;

    if (projectPrefix) {
      const match = candidates.find(f => (f.level_name || '').toLowerCase().includes(projectPrefix));
      if (match) return match;
    }

    // Fallback: pick the first one from the same PDF
    return candidates[0];
  }

  getOptionDeltaValue(option: EnhancedFormula, row: any): string {
    const optionRaw = this.getDisplayRowValue(option, row);
    if (optionRaw === '-') return '-';

    const base = this.findBaseForOption(option);
    if (!base) return optionRaw; // Fallback if no base found

    const baseRaw = this.getDisplayRowValue(base, row);
    if (baseRaw === '-') return optionRaw;

    // Try to calculate delta for percentages (e.g. "125% BR" - "100% BR")
    const extractNum = (s: string) => {
      const m = s.match(/(\d+(?:[.,]\d+)?)/);
      return m ? parseFloat(m[1].replace(',', '.')) : null;
    };

    const optVal = extractNum(optionRaw);
    const baseVal = extractNum(baseRaw);

    if (optVal !== null && baseVal !== null && optVal > baseVal) {
      const delta = optVal - baseVal;
      // Preserve the unit/suffix if possible
      const suffix = optionRaw.replace(/[\d.,\s]+/g, '').trim();
      // Special case: if base is 100% and opt is 125%, delta is 25%
      return `+${delta}${suffix ? ' ' + suffix : ''}`;
    }

    // If it already starts with + or we can't calculate, return as is
    if (optionRaw.trim().startsWith('+')) return optionRaw;

    return optionRaw;
  }

  getHiddenFormulasCount(): number {
    return Math.max(0, this.getFlatFormulas(false).length - this.maxVisibleColumns);
  }

  changeSort(value: SortType): void {
    this.selectedSort = value;
  }

  clearAll(): void {
    this.extractions = [];
    this.comparisonSourceFiles = [];
    this.detectedClientName = '';
    this.selectedSort = 'recommended';
  }

  loadHistoryItem(item: ComparisonHistoryItem): void {
    this.extractions = item.extractions || [];
    this.detectedClientName = item.clientName || '';
    this.selectedSort = 'recommended';
  }

  removeHistoryItem(itemId: string): void {
    if (!isNaN(Number(itemId))) {
      this.enterpriseHistoryService.deleteHistory(itemId).subscribe(() => {
        this.enterpriseHistoryService.loadHistory();
      });
    }
    this.comparisonHistory = this.comparisonHistory.filter((item) => item.id !== itemId);
    this.persistHistory();
  }

  exportCurrentComparison(): void {
    if (this.extractions.length === 0) return;
    const payload = {
      exportedAt: new Date().toISOString(),
      clientName: this.detectedClientName || this.resolveClientNameFromBatch(this.extractions),
      totalFormulas: this.getFlatFormulas().length,
      extractions: this.extractions
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparison_${(payload.clientName || 'client').replace(/\s+/g, '_')}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  openSendEmailDialog(): void {
    if (!this.canShowComparisonTable()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Comparaison indisponible',
        detail: 'Le tableau comparatif doit être affiché avant l’envoi par email.'
      });
      return;
    }
    const formulas = this.getVisibleFormulas();
    if (formulas.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aucune formule',
        detail: 'Aucune colonne à envoyer.'
      });
      return;
    }
    this.emailTableHtmlRaw = this.buildEnterpriseEmailComparisonTableHtml();
    this.emailTablePreview = this.domSanitizer.bypassSecurityTrustHtml(this.emailTableHtmlRaw);
    // Si aucun email client n'est extrait, on laisse volontairement le champ vide.
    this.recipientEmailEntreprise = this.resolveClientEmailForEmailDialog();
    this.emailClientDisplayName = this.resolveContactNameForEmail();
    this.emailClientCivility = this.normalizeCivilityValue(this.resolveCivilityFromExtractions());
    this.showEmailDialog = true;
  }

  closeSendEmailDialog(): void {
    this.showEmailDialog = false;
    this.sendingEnterpriseEmail = false;
  }

  openDemandeInfoDialog(): void {
    this.demandeInfoClientName = this.resolveContactNameForEmail();
    this.demandeInfoCivility = this.normalizeCivilityValue(this.resolveCivilityFromExtractions());
    this.demandeInfoCompanyName = this.resolveCompanyNameFromBatch(this.extractions)
      || this.detectedClientName
      || '';
    this.demandeInfoRecipientEmail = this.resolveClientEmailForEmailDialog();
    this.showDemandeInfoDialog = true;
  }

  closeDemandeInfoDialog(): void {
    this.showDemandeInfoDialog = false;
    this.sendingDemandeInfo = false;
  }

  confirmSendDemandeInfo(): void {
    const email = (this.demandeInfoRecipientEmail || '').trim();
    if (!email) {
      this.messageService.add({
        severity: 'error',
        summary: 'Email requis',
        detail: 'Veuillez saisir l\'adresse email du destinataire.'
      });
      return;
    }
    const simple = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!simple.test(email)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Email invalide',
        detail: 'Vérifiez le format de l\'adresse email.'
      });
      return;
    }

    const clientName = (this.demandeInfoClientName || '').trim() || 'Client';
    const nameParts = this.splitClientNameForEmail(clientName);

    let currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      const username = localStorage.getItem('username') || '';
      const uemail = localStorage.getItem('email') || '';
      const telephone = localStorage.getItem('telephone') || '';
      const telephone_fixe = localStorage.getItem('telephone_fixe') || '';
      const photo_url = localStorage.getItem('photo_url') || '';
      currentUser = {
        id: '0', username, email: uemail, role: 'USER', status: 'ACTIVE',
        telephone, telephone_fixe, photo_url
      } as any;
    }

    const payload = {
      recipientEmail: email,
      recipientFirstName: nameParts.firstName,
      recipientLastName: nameParts.lastName,
      recipientCivility: this.normalizeCivilityValue(this.demandeInfoCivility),
      companyName: (this.demandeInfoCompanyName || '').trim() || 'Votre entreprise',
      phone: '',
      needs: {
        siret: (this.demandeInfoSiret || '').trim(),
        gerant: (this.demandeInfoGerant || '').trim(),
        salarie: (this.demandeInfoSalarie || '').trim(),
        ccn: (this.demandeInfoCcn || '').trim()
      } as Record<string, string>,
      offers: [],
      conseillerNom: currentUser?.username || `${(currentUser as any)?.prenom || ''} ${(currentUser as any)?.nom || ''}`.trim() || '',
      conseillerEmail: currentUser?.email || '',
      conseillerTelephone: currentUser?.telephone || '',
      conseillerTelephoneFixe: (currentUser as any)?.telephone_fixe || '',
      conseillerPhotoUrl: (currentUser as any)?.photo_url || ''
    };

    this.sendingDemandeInfo = true;
    const endpoint = `${environment.casApiUrl}/api/v1/devis/email/demande-info`;
    this.http.post(endpoint, payload).subscribe({
      next: () => {
        this.sendingDemandeInfo = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Email envoyé',
          detail: `La demande d'information a été envoyée à ${email}.`
        });
        this.closeDemandeInfoDialog();
      },
      error: (err: any) => {
        this.sendingDemandeInfo = false;
        const msg = err?.error?.message || err?.message || 'Impossible d\'envoyer l\'email.';
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: msg
        });
      }
    });
  }

  openEconomiserEmailDialog(): void {
    this.economiserClientName = this.resolveContactNameForEmail();
    this.economiserCivility = this.normalizeCivilityValue(this.resolveCivilityFromExtractions()) || 'Monsieur';
    this.economiserRecipientEmail = this.resolveClientEmailForEmailDialog();
    this.economiserAssurance = '';
    this.economiserRef = '';
    this.economiserProduit = '';
    this.showEconomiserEmailDialog = true;
  }

  closeEconomiserEmailDialog(): void {
    this.showEconomiserEmailDialog = false;
    this.sendingEconomiserEmail = false;
  }

  confirmSendEconomiserEmail(): void {
    const email = (this.economiserRecipientEmail || '').trim();
    if (!email) {
      this.messageService.add({
        severity: 'error',
        summary: 'Email requis',
        detail: 'Veuillez saisir l\'adresse email du destinataire.'
      });
      return;
    }
    const simple = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!simple.test(email)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Email invalide',
        detail: 'Vérifiez le format de l\'adresse email.'
      });
      return;
    }

    const clientName = (this.economiserClientName || '').trim() || 'Client';
    const nameParts = this.splitClientNameForEmail(clientName);

    let currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      const username = localStorage.getItem('username') || '';
      const uemail = localStorage.getItem('email') || '';
      const telephone = localStorage.getItem('telephone') || '';
      const telephone_fixe = localStorage.getItem('telephone_fixe') || '';
      const photo_url = localStorage.getItem('photo_url') || '';
      currentUser = {
        id: '0', username, email: uemail, role: 'USER', status: 'ACTIVE',
        telephone, telephone_fixe, photo_url
      } as any;
    }

    const payload = {
      recipientEmail: email,
      recipientFirstName: nameParts.firstName,
      recipientLastName: nameParts.lastName,
      recipientCivility: this.normalizeCivilityValue(this.economiserCivility),
      companyName: this.resolveCompanyNameFromBatch(this.extractions) || this.detectedClientName || 'Votre entreprise',
      phone: '',
      needs: {
        assurance: (this.economiserAssurance || '').trim(),
        ref: (this.economiserRef || '').trim(),
        produit: (this.economiserProduit || '').trim()
      } as Record<string, string>,
      offers: [],
      conseillerNom: currentUser?.username || `${(currentUser as any)?.prenom || ''} ${(currentUser as any)?.nom || ''}`.trim() || '',
      conseillerEmail: currentUser?.email || '',
      conseillerTelephone: currentUser?.telephone || '',
      conseillerTelephoneFixe: (currentUser as any)?.telephone_fixe || '',
      conseillerPhotoUrl: (currentUser as any)?.photo_url || ''
    };

    this.sendingEconomiserEmail = true;
    const endpoint = `${environment.casApiUrl}/api/v1/devis/email/economiser`;
    this.http.post(endpoint, payload).subscribe({
      next: () => {
        this.sendingEconomiserEmail = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Email envoyé',
          detail: `L'email ECONOMISER a été envoyé à ${email}.`
        });
        this.closeEconomiserEmailDialog();
      },
      error: (err: any) => {
        this.sendingEconomiserEmail = false;
        const msg = err?.error?.message || err?.message || 'Impossible d\'envoyer l\'email.';
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: msg
        });
      }
    });
  }

  async confirmSendEnterpriseEmail(type: 'particulier' | 'entreprise' = 'entreprise'): Promise<void> {
    const email = (this.recipientEmailEntreprise || '').trim();
    if (!email) {
      this.messageService.add({
        severity: 'error',
        summary: 'Email requis',
        detail: 'Veuillez saisir l’adresse email du destinataire.'
      });
      return;
    }
    const simple = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!simple.test(email)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Email invalide',
        detail: 'Vérifiez le format de l’adresse email.'
      });
      return;
    }

    const clientNameForEmail = (this.emailClientDisplayName || '').trim()
      || this.resolveContactNameForEmail()
      || (this.detectedClientName || '').trim()
      || 'Client';
    const nameParts = this.splitClientNameForEmail(clientNameForEmail);
    const recipientFirstName = nameParts.firstName;
    const recipientLastName = nameParts.lastName;
    const recipientCivility = this.normalizeCivilityValue(this.emailClientCivility);

    const formulas = this.getVisibleFormulas();
    const offers = formulas.map((f) => ({
      insurer: f.insurer,
      productName: f.contract,
      formula: f.level_name,
      price: f.premiums['isole'] ?? null,
      correspondencePercentage: Math.round(f.coverageScore ?? 0),
      weakPoint: '',
      guarantees: this.buildEnterpriseGuaranteesForFormula(f)
    }));

    let currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      const username = localStorage.getItem('username') || '';
      const uemail = localStorage.getItem('email') || '';
      const telephone = localStorage.getItem('telephone') || '';
      const telephone_fixe = localStorage.getItem('telephone_fixe') || '';
      const photo_url = localStorage.getItem('photo_url') || '';
      currentUser = {
        id: '0',
        username,
        email: uemail,
        role: 'USER',
        status: 'ACTIVE',
        telephone,
        telephone_fixe,
        photo_url
      } as any;
    }
    const conseillerNom = currentUser?.username || `${(currentUser as any)?.prenom || ''} ${(currentUser as any)?.nom || ''}`.trim() || '';
    const conseillerEmail = currentUser?.email || '';
    const conseillerTelephone = currentUser?.telephone || '';
    const conseillerTelephoneFixe = (currentUser as any)?.telephone_fixe || '';
    const conseillerPhotoUrl = (currentUser as any)?.photo_url || '';

    const companyName = this.resolveCompanyNameFromBatch(this.extractions)
      || this.detectedClientName
      || 'Votre entreprise';

    const comparisonAttachments = await this.buildComparisonEmailAttachments();

    const payload = {
      recipientEmail: email,
      recipientFirstName,
      recipientLastName,
      recipientCivility,
      companyName,
      phone: '',
      needs: {} as Record<string, string>,
      offers,
      comparisonTable: this.emailTableHtmlRaw,
      conseillerNom,
      conseillerEmail,
      conseillerTelephone,
      conseillerTelephoneFixe,
      conseillerPhotoUrl,
      ...(comparisonAttachments.length ? { attachments: comparisonAttachments } : {})
    };

    this.sendingEnterpriseEmail = true;
    const endpoint = `${environment.casApiUrl}/api/v1/devis/email/${type}`;
    this.http.post(endpoint, payload).subscribe({
      next: () => {
        this.sendingEnterpriseEmail = false;
        const pjMsg = comparisonAttachments.length
          ? ` avec ${comparisonAttachments.length} PDF de comparaison en pièce(s) jointe(s)`
          : '';
        this.messageService.add({
          severity: 'success',
          summary: 'Email envoyé',
          detail: `Le comparatif a été envoyé à ${email}${pjMsg}.`
        });
        this.closeSendEmailDialog();
      },
      error: (err: any) => {
        this.sendingEnterpriseEmail = false;
        const msg = err?.error?.message || err?.message || 'Impossible d’envoyer l’email.';
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: msg
        });
      }
    });
  }

  private buildEnterpriseEmailComparisonTableHtml(): string {
    const baseFormulas = this.getVisibleFormulas();
    const optionFormulas = this.getVisibleOptionFormulas();
    const clientLabel = this.resolveCompanyNameFromBatch(this.extractions) || this.detectedClientName || this.resolveClientNameFromBatch(this.extractions) || 'votre entreprise';
    const esc = (v: unknown) => this.escapeHtml(v == null ? '' : String(v));

    const fmtMoney = (n: number | undefined | null, prefix = '') => {
      if (n == null || !isFinite(n)) return esc('-');
      const val = n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
      return prefix + val;
    };

    const benefitStyleByClass = (coverageClass: string): string => {
      switch (coverageClass) {
        case 'coverage-excellent': return 'background-color:#059669;color:#FFFFFF;font-weight:bold;';
        case 'coverage-good': return 'background-color:#22C55E;color:#FFFFFF;font-weight:bold;';
        case 'coverage-medium': return 'background-color:#F59E0B;color:#FFFFFF;font-weight:bold;';
        case 'coverage-low': return 'background-color:#EA580C;color:#FFFFFF;font-weight:bold;';
        default: return 'background-color:#6B7280;color:#FFFFFF;font-weight:bold;';
      }
    };

    const buildTable = (targetFormulas: any[], isRenfortTable: boolean) => {
      const tableWidth = 800;
      const firstColWidth = 140;
      const remainingWidth = tableWidth - firstColWidth;
      const colWidth = Math.floor(remainingWidth / targetFormulas.length);
      const mainColor = "#A102F2";
      const borderStyle = "1px solid #E9D5FF";

      let h = `<thead><tr><th width="${firstColWidth}" style="padding:10px 8px;border:${borderStyle};background-color:${mainColor};color:#FFFFFF;text-align:left;font-size:10px;font-weight:bold;width:${firstColWidth}px;">Assureur</th>`;
      for (const f of targetFormulas) {
        const name = f ? f.insurer.toUpperCase() : '-';
        h += `<th width="${colWidth}" style="padding:10px 8px;border:${borderStyle};background-color:${mainColor};color:#FFFFFF;text-align:center;font-size:10px;font-weight:bold;width:${colWidth}px;">` +
          `<div style="font-weight:bold;">${esc(name)}</div>` +
          (f?.isCurrentContract ? `<div style="margin-top:4px;font-size:8px;color:#065F46;background-color:#D1FAE5;padding:2px 4px;display:inline-block;border:1px solid #6EE7B7;">Contrat actuel</div>` : '') +
          `</th>`;
      }
      h += '</tr></thead>';

      const drawRow = (label: string, cells: string[], bg = '#FFFFFF', isBold = false, allowHtmlInLabel = false) => {
        const labelHtml = allowHtmlInLabel ? label : esc(label);
        let r = `<tr bgcolor="${bg}"><td width="${firstColWidth}" style="padding:8px 10px;border:${borderStyle};background-color:${bg};font-weight:bold;color:#111827;text-align:left;font-size:10px;width:${firstColWidth}px;">${labelHtml}</td>`;
        for (const c of cells) {
          const cellStyle = `padding:8px;border:${borderStyle};background-color:${bg};text-align:center;font-size:10px;color:#111827;width:${colWidth}px;${isBold ? 'font-weight:bold;' : ''}`;
          r += `<td width="${colWidth}" style="${cellStyle}">${c}</td>`;
        }
        r += '</tr>';
        return r;
      };

      const drawBenefitRow = (label: string, row: BenefitRow, bg = '#FFFFFF') => {
        let r = `<tr bgcolor="${bg}"><td width="${firstColWidth}" style="padding:8px 10px;border:${borderStyle};background-color:${bg};font-weight:bold;color:#111827;text-align:left;font-size:10px;width:${firstColWidth}px;">${esc(label)}</td>`;
        for (const f of targetFormulas) {
          if (!f) {
            r += `<td width="${colWidth}" style="padding:8px;border:${borderStyle};background-color:#F9FAFB;text-align:center;font-size:10px;color:#9CA3AF;width:${colWidth}px;">-</td>`;
          } else {
            let rawVal = isRenfortTable ? this.getOptionDeltaValue(f, row) : this.getDisplayRowValue(f, row);
            const style = benefitStyleByClass(this.getBenefitClass(rawVal));

            // Add '+' prefix for renfort table if missing and value is not '-'
            if (isRenfortTable && rawVal !== '-' && !rawVal.trim().startsWith('+')) {
              rawVal = '+ ' + rawVal;
            }

            const emailValue = rawVal.replace('class="calc-hint"', 'style="display:block;font-size:8px;opacity:0.8;margin-top:2px;"');
            r += `<td width="${colWidth}" style="padding:8px;border:${borderStyle};text-align:center;font-size:10px;width:${colWidth}px;${style}">${emailValue}</td>`;
          }
        }
        r += '</tr>';
        return r;
      };

      let b = '<tbody>';
      const prefix = isRenfortTable ? '+ ' : '';
      b += drawRow('Produit', targetFormulas.map(f => f ? `<strong>${esc(this.cleanFormulaName(f.contract))}</strong>` : '-'), '#F8F5FF');
      b += drawRow('Formule', targetFormulas.map(f => f ? `<i>${esc(this.cleanFormulaName(f.level_name))}</i>` : '-'));

      this.displayRows.forEach((row, i) => {
        b += drawBenefitRow(row.label, row, i % 2 === 0 ? '#FFFFFF' : '#FAFAFA');
      });

      const isolLabel = isRenfortTable ? 'Prix add. total : Employeur + Salarié' : 'Prix total : Employeur + Salarié';
      const isolSub = isRenfortTable
        ? '<br><span style="font-size:8px;font-weight:normal;color:#6B7280;">Add. Solo : Salarié seul</span>'
        : '<br><span style="font-size:8px;font-weight:normal;color:#6B7280;">Solo : Salarié seul</span>';
      b += drawRow(isolLabel + isolSub, targetFormulas.map(f => f ? `<div style="color:${mainColor};font-weight:bold;">${fmtMoney(f.premiums['isole'], prefix)}</div><div style="font-size:8px;color:#6B7280;margin-top:2px;">/ mois</div>` : '-'), '#FEFCE8', true, true);

      const duoLabel = isRenfortTable ? 'Add. Duo' : 'Prix Duo';
      const duoSub = ' <span style="font-size:8px;font-weight:normal;color:#6B7280;">(Salarié + Conjoint ou + 1 enfant)</span>';
      b += drawRow(duoLabel + duoSub, targetFormulas.map(f => f ? `<div style="color:${mainColor};font-weight:bold;">${fmtMoney(f.premiums['duo'], prefix)}</div><div style="font-size:8px;color:#6B7280;margin-top:2px;">/ mois</div>` : '-'), '#FFFFFF', true, true);

      const famLabel = isRenfortTable ? 'Add. Famille' : 'Prix Famille';
      const famSub = ' <span style="font-size:8px;font-weight:normal;color:#6B7280;">(Salarié + Conjoint + Enfants)</span>';
      b += drawRow(famLabel + famSub, targetFormulas.map(f => f ? `<div style="color:${mainColor};font-weight:bold;">${fmtMoney(f.premiums['famille'], prefix)}</div><div style="font-size:8px;color:#6B7280;margin-top:2px;">/ mois</div>` : '-'), '#F9FAFB', true, true);
      b += '</tbody>';

      const title = isRenfortTable ? 'Options et Renforts' : 'Garanties Principales';

      return `<div align="center" style="margin-top:20px;">` +
        `<h3 style="font-size:15px;color:${mainColor};margin-bottom:10px;text-align:left;width:${tableWidth}px;">${title}</h3>` +
        `<table width="${tableWidth}" cellpadding="0" cellspacing="0" border="0" style="width:${tableWidth}px;border-collapse:collapse;background-color:#FFFFFF;table-layout:fixed;font-family:Arial,sans-serif;">` +
        h + b + '</table></div>';
    };

    const baseTable = buildTable(baseFormulas, false);
    const renfortTable = optionFormulas.length > 0 ? buildTable(optionFormulas, true) : '';

    return `<div style="font-family:Arial, sans-serif;">` +
      baseTable + renfortTable + `</div>`;
  }

  getDisplayRowValue(formula: any, row: BenefitRow): string {
    let value = '-';
    for (const path of row.paths) {
      const [cat, key] = path.split('.');
      const v = formula?.benefits?.[cat]?.[key];
      if (v !== undefined && v !== null && v !== '') {
        value = String(v);
        break;
      }
    }
    return this.formatBenefitValue(value, row);
  }

  private formatBenefitValue(value: string, row?: BenefitRow): string {
    if (!value || value === '-' || value.toLowerCase() === 'néant' || value.toLowerCase() === 'neant') {
      return '0';
    }

    const v = value.trim();
    if (v === 'FR' || v.toLowerCase().includes('frais réels')) return 'FRAIS RÉELS';

    const passPattern = /([\d.,]+)\s*%\s*(PMSS|PASS)(.*)/i;
    const match = v.match(passPattern);
    if (match) {
      const percentage = parseFloat(match[1].replace(',', '.'));
      const isPmss = match[2].toUpperCase() === 'PMSS';
      const suffix = (match[3] || '').trim();
      const referenceValue = isPmss ? (this.passValue / 12) : this.passValue;
      const calculatedAmount = (referenceValue * percentage) / 100;
      const formattedAmount = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      }).format(calculatedAmount);
      const displayEuro = suffix ? `${formattedAmount} ${suffix}` : formattedAmount;
      return `${displayEuro} <span class="calc-hint">(${this.escapeHtml(v)})</span>`;
    }

    if (/^\d+([.,]\d+)?$/.test(v) && row) {
      const lbl = row.label.toLowerCase();
      const isEuro = lbl.includes('chambre') || lbl.includes('forfait') || lbl.includes('medecine') || lbl.includes('médecine');
      return isEuro ? `${v} €` : `${v} %`;
    }

    return v;
  }

  private buildEnterpriseGuaranteesForFormula(f: EnhancedFormula): Record<string, string> {
    const map: Record<string, string> = {};
    this.displayRows.forEach(row => {
      map[row.label] = this.getDisplayRowValue(f, row).replace(/<[^>]*>/g, '').trim();
    });
    return map;
  }

  private resolveClientEmailForEmailDialog(): string {
    for (const ext of this.extractions) {
      const email = ext.client_email || ext.client_identity?.email;
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return email;
    }
    return '';
  }

  private resolveContactNameForEmail(): string {
    const ordered = [...this.extractions].sort((a, b) => (a.isCurrentContract ? 0 : 1) - (b.isCurrentContract ? 0 : 1));
    for (const ext of ordered) {
      let person = (ext.interlocuteur || '').trim() || (ext.proposant || '').trim() || (ext.nom || '').trim();
      if (!person && ext.client_identity) {
        const id = ext.client_identity;
        person = (id.interlocuteur || '').trim() || (id.proposant || '').trim() || (id.nom || '').trim();
      }
      if (this.isLikelyPersonName(person)) {
        return this.extractLastName(person);
      }
    }
    return '';
  }

  /**
   * Extrait le nom de famille d'une chaîne et retire les civilités (M., Mme, etc.)
   */
  private extractLastName(fullName: string): string {
    if (!fullName) return '';
    // 1. Nettoyage très agressif des titres et civilités
    let name = fullName.replace(/\b(M\.|M\.\s|Mme|MME|Mr|MR|Mlle|Monsieur|Madame|Mademoiselle)\b/gi, '').trim();
    name = name.replace(/^M\s+/i, '').trim(); // Cas "M MARIANI"

    // 2. Découpage en mots
    const parts = name.split(/\s+/).filter(p => p.length > 0);
    if (parts.length === 0) return '';

    // 3. Heuristique : si un mot est tout en majuscules (et > 1 lettre), c'est probablement le NOM
    const upperPart = parts.find(p => p.length > 1 && p === p.toUpperCase() && /[A-Z]/.test(p));
    if (upperPart) return this.capitalize(upperPart);

    // 4. Sinon, on prend le premier mot par défaut (souvent le cas dans NOM Prénom)
    return this.capitalize(parts[0]);
  }

  private capitalize(s: string): string {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }

  private resolveCivilityFromExtractions(): string {
    // On parcourt toutes les extractions pour trouver une civilité non vide.
    // On privilégie celle qui est explicitement remplie (Monsieur ou Madame).
    for (const ext of this.extractions) {
      const civ = (ext.client_civility || ext.client_identity?.civilite || '').trim();
      if (civ && (civ.toLowerCase().includes('m') || civ.toLowerCase().includes('monsieur') || civ.toLowerCase().includes('madame'))) {
        return civ;
      }
    }
    return '';
  }

  private normalizeCivilityValue(civ: string): '' | 'Monsieur' | 'Madame' {
    if (!civ) return '';
    const low = civ.toLowerCase().trim();

    // Check for Madame first (Mme, Madame)
    if (low === 'mme' || low === 'madame' || low.startsWith('mme.')) return 'Madame';

    // Check for Monsieur (M., Mr, Monsieur)
    // Be careful with "M." to not match "Mme" if it starts with M.
    if (low === 'm.' || low === 'mr' || low === 'mr.' || low === 'monsieur' || low === 'm') return 'Monsieur';

    // Fallback search
    if (low.includes('madame')) return 'Madame';
    if (low.includes('monsieur')) return 'Monsieur';

    return '';
  }

  private splitClientNameForEmail(name: string): { firstName: string, lastName: string } {
    const parts = (name || '').trim().split(/\s+/);
    if (parts.length <= 1) return { firstName: '', lastName: name || '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }

  formatGuaranteeName(name: string): string {
    if (!name) return '';
    return name.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  cleanFormulaName(name: string): string {
    if (!name) return '';
    let cleaned = name.replace(/^[+\s]+/, '').replace(/[+\s]+$/, '').trim();
    
    // Normalisation pour les produits de Prévoyance (souvent extraits avec des suffixes Santé)
    if (cleaned.toUpperCase().includes('PREVOYANCE')) {
       cleaned = cleaned.replace(/\s*-\s*Santé/gi, '')
                        .replace(/\s+Santé/gi, '')
                        .replace(/Santé\s+/gi, '');
    }
    
    // Suppression générique du suffixe "- Santé" en fin de chaîne
    cleaned = cleaned.replace(/\s*-\s*Santé$/i, '');

    return cleaned.trim();
  }

  getBenefitClass(val: string): string {
    if (!val || val === '-') return 'coverage-none';
    const lower = val.toLowerCase().replace(/\s+/g, '');
    if (lower.includes('fraisréels') || lower.includes('fr')) return 'coverage-excellent';
    const match = lower.match(/(\d+)/);
    if (!match) return 'coverage-none';
    const num = parseInt(match[1], 10);
    if (num >= 200) return 'coverage-excellent';
    if (num >= 100) return 'coverage-good';
    if (num >= 50) return 'coverage-medium';
    if (num > 0) return 'coverage-low';
    return 'coverage-none';
  }

  private extractClientName(fileName: string): string {
    const normalized = (fileName || '').replace(/\.pdf$/i, '').replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
    const markerMatch = normalized.match(/(?:devis|brokins)\s+(.+?)(?:\s*-\s*formule|\s+formule|$)/i);
    const raw = markerMatch?.[1] || normalized;
    return raw.replace(/(alptis|swisslife|april|aesio|malakoff|harmonie|neoliane)/gi, '').replace(/\b(formule|option|niveau)\b.*$/i, '').trim().toUpperCase();
  }

  private resolveClientDisplayNameFromExtraction(ext: EnterpriseExtraction, fileName: string): string {
    const person = (ext.interlocuteur || '').trim() || (ext.proposant || '').trim() || (ext.nom || '').trim() || (ext.client_identity?.interlocuteur || '').trim();
    return this.isLikelyPersonName(person) ? person : '';
  }

  private isLikelyPersonName(v: string): boolean {
    if (!v || v.length < 3) return false;
    const low = v.toLowerCase();
    const blocked = ['coordonnée', 'coordonnee', 'tél', 'email', 'adresse', 'siret'];
    return !blocked.some(k => low.includes(k)) && !v.includes('@') && !/\d{3,}/.test(v) && /[a-zA-ZÀ-ÿ]/.test(v);
  }

  private resolveCompanyNameFromBatch(batch: EnterpriseExtraction[]): string {
    for (const ext of batch) {
      const rs = (ext.raison_sociale || ext.client_identity?.raison_sociale || '').trim();
      if (rs) return rs;
    }
    return '';
  }

  private resolveClientNameFromBatch(batch: EnterpriseExtraction[]): string {
    return batch.map(i => i.clientName || '').filter(n => !!n)[0] || '';
  }

  public saveCurrentComparisonToHistory(manual = false): void {
    const count = this.extractions.length;
    if (count < 1) return;

    console.log('💾 Tentative de sauvegarde historique entreprise...', { count, manual });

    const hasContract = this.extractions.some(e => !!e.isCurrentContract);

    const item: ComparisonHistoryItem = {
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      clientName: this.detectedClientName || this.resolveCompanyNameFromBatch(this.extractions) || this.resolveClientNameFromBatch(this.extractions) || 'Client inconnu',
      formulasCount: this.getFlatFormulas().length,
      includesCurrentContract: hasContract,
      extractions: JSON.parse(JSON.stringify(this.extractions))
    };

    // 1. Local Storage
    this.comparisonHistory = [item, ...this.comparisonHistory].slice(0, 20);
    this.persistHistory();

    // 2. 📡 SYNC WITH DATABASE (MySQL - NEW TABLE)
    const currentUser = this.authService.getCurrentUser();
    const payload = {
      userName: currentUser?.username || 'Utilisateur',
      userEmail: currentUser?.email || '',
      clientName: item.clientName,
      formulasCount: item.formulasCount,
      includesCurrentContract: item.includesCurrentContract,
      extractions: item.extractions
    };

    console.log('📡 Envoi du payload au serveur:', payload);

    this.enterpriseHistoryService.saveComparison(payload).subscribe({
      next: (res) => {
        console.log('✅ Sauvegarde DB réussie:', res);
        this.enterpriseHistoryService.loadHistory();
        if (manual) {
          this.messageService.add({ severity: 'success', summary: 'Sauvegardé', detail: 'Comparaison enregistrée dans l\'historique.' });
        }
      },
      error: (err) => {
        console.error('❌ Erreur sauvegarde historique entreprise:', err);
        if (manual) {
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: 'Impossible de sauvegarder la comparaison.' });
        }
      }
    });
  }

  private loadHistory(): void {
    // 1. Load from LocalStorage (Legacy)
    const stored = localStorage.getItem(this.historyStorageKey);
    if (stored) {
      try {
        this.comparisonHistory = JSON.parse(stored);
      } catch (e) {
        console.error('Erreur parsing local history:', e);
        this.comparisonHistory = [];
      }
    }

    // 2. Load from Database (MySQL - NEW TABLE)
    this.enterpriseHistoryService.getHistory().subscribe(dbHistory => {
      const dbEnterpriseItems: ComparisonHistoryItem[] = dbHistory.map(h => ({
        id: h.id.toString(),
        createdAt: h.dateComparaison,
        clientName: h.clientName,
        formulasCount: h.formulasCount,
        includesCurrentContract: h.includesCurrentContract,
        extractions: typeof h.extractionsJson === 'string' ? JSON.parse(h.extractionsJson) : h.extractionsJson
      }));

      // Merge avoiding duplicates (prefer DB) and filter out invalid items
      const existingIds = new Set(dbEnterpriseItems.map(i => i.id));
      this.comparisonHistory = [
        ...dbEnterpriseItems,
        ...this.comparisonHistory.filter(i => !existingIds.has(i.id))
      ].filter(i => i.formulasCount !== undefined && i.formulasCount !== null && i.createdAt !== undefined)
       .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
       .slice(0, 20);
       
      this.persistHistory();
    });
  }
  private persistHistory(): void {
    localStorage.setItem(this.historyStorageKey, JSON.stringify(this.comparisonHistory));
  }

  private escapeHtml(v: unknown): string {
    const s = v == null ? '' : String(v);
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  private async buildComparisonEmailAttachments(): Promise<Array<{
    filename: string;
    content_type: string;
    content_base64: string;
  }>> {
    const attachments: Array<{ filename: string; content_type: string; content_base64: string }> = [];
    const seen = new Set<string>();

    for (const file of this.comparisonSourceFiles) {
      if (!file || file.type !== 'application/pdf') continue;
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      if (seen.has(key)) continue;
      seen.add(key);
      try {
        const base64 = await this.blobToBase64(file);
        attachments.push({
          filename: file.name.toLowerCase().endsWith('.pdf') ? file.name : `${file.name}.pdf`,
          content_type: 'application/pdf',
          content_base64: base64
        });
      } catch (e) {
        console.warn('[Email PDF] Impossible de joindre le PDF entreprise:', file.name, e);
      }
    }
    return attachments;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
